import { useMemo } from "react";
import { useStore } from "../../../helpers";
import { useRole } from "../../../custom-hooks/useRole";
import { defaultFieldTeamReportTables } from "../utils";

// Types
type UserRole = "root" | "client" | "superadmin" | "partyworker" | "backoffice" | "surveyworker" | "qaworker";

interface TableConfig {
  enabled: boolean;
  roles: UserRole[];
}

/**
 * Hook to check if a table should be visible based on configuration and user role
 * 
 * @param tableKey - The key of the table in the configuration
 * @returns boolean indicating if the table should be shown
 * 
 * @example
 * const isVisible = useTableVisibility("surveys_by_user");
 * if (!isVisible) return null;
 */
export const useTableVisibility = (tableKey: string): boolean => {
  const { projectStore } = useStore();
  const userRole = useRole() as UserRole | string;

  // Use useMemo to compute visibility
  // Include projectStore.activeProject in dependencies to ensure re-computation when settings change
  return useMemo(() => {
    try {
      // Early return if user role is not available
      if (!userRole || typeof userRole !== "string") {
        return false;
      }

      // Get the table configuration from project properties
      const allTablesConfig = projectStore.getProjectProperty(
        "field_team_report_tables",
        defaultFieldTeamReportTables
      );

      // Get specific table config or fall back to defaults
      const tableConfig: TableConfig | undefined = 
        allTablesConfig?.[tableKey] || defaultFieldTeamReportTables[tableKey];

      // Default to showing the table if no config found (backward compatibility)
      if (!tableConfig) {
        return true;
      }

      // Check if table is enabled - this applies to ALL users including root
      if (!tableConfig.enabled) {
        return false;
      }

      // Root users bypass role checks but still respect the enabled flag above
      if (userRole === "root") {
        return true;
      }

      // Check if user's role is in the allowed roles array
      return tableConfig.roles?.includes(userRole as UserRole) || false;
    } catch (error) {
      console.error('Error in useTableVisibility:', error);
      return true; // Default to visible on error
    }
  }, [projectStore.activeProject, tableKey, userRole]);
};
